import React from 'react';
import { Box, Typography, Card, CardContent, Grid, CardMedia, IconButton, Button } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import LinkIcon from '@mui/icons-material/Link';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { motion, AnimatePresence } from 'framer-motion';
import { VideoMetadata } from '../services/api';

interface DownloadHistoryProps {
  history: Array<VideoMetadata & { timestamp: number }>;
  onItemClick: (item: VideoMetadata) => void;
  onClearHistory: () => void;
  onDeleteItem?: (item: VideoMetadata) => void;
}

const MediaGrid: React.FC<{ item: VideoMetadata }> = ({ item }) => {
  const totalMedia = item.videos.length + (item.images?.length || 0);
  const allMedia = [
    ...item.videos.map(v => ({ type: 'video', url: v.thumbnail || '' })),
    ...(item.images?.map(i => ({ type: 'image', url: i.url })) || [])
  ];

  if (totalMedia === 1) {
    // Single media layout
    return (
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component="img"
          sx={{
            height: 200,
            objectFit: 'cover',
          }}
          image={allMedia[0].url}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: 'white',
            opacity: 0.7
          }}
        >
          {allMedia[0].type === 'video' ? (
            <PlayCircleIcon sx={{ fontSize: 34 }} />
          ) : (
            <CameraAltIcon sx={{ fontSize: 34 }} />
          )}
        </Box>
      </Box>
    );
  }

  if (totalMedia === 2) {
    // Two column layout
    return (
      <Grid container spacing={1}>
        {allMedia.map((media, index) => (
          <Grid item xs={6} key={index}>
            <Box sx={{ position: 'relative' }}>
              <CardMedia
                component="img"
                sx={{
                  height: 200,
                  objectFit: 'cover',
                }}
                image={media.url}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: 'white',
                  opacity: 0.7
                }}
              >
                {media.type === 'video' ? (
                  <PlayCircleIcon sx={{ fontSize: 34 }} />
                ) : (
                  <CameraAltIcon sx={{ fontSize: 34 }} />
                )}
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  }

  if (totalMedia === 3) {
    // For 3 media items, only show the first large image
    return (
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component="img"
          sx={{
            height: 200,
            objectFit: 'cover',
          }}
          image={allMedia[0].url}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: 'white',
            opacity: 0.7
          }}
        >
          {allMedia[0].type === 'video' ? (
            <PlayCircleIcon sx={{ fontSize: 34 }} />
          ) : (
            <CameraAltIcon sx={{ fontSize: 34 }} />
          )}
        </Box>
        {/* Indicator for additional media */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            color: 'white',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 'medium' }}>
            +2
          </Typography>
          {allMedia[1].type === 'video' ? <PlayCircleIcon sx={{ fontSize: 14 }} /> : <CameraAltIcon sx={{ fontSize: 14 }} />}
        </Box>
      </Box>
    );
  }

  // Four or more media (2x2 grid)
  return (
    <Grid container spacing={0.5}>
      {allMedia.slice(0, 4).map((media, index) => (
        <Grid item xs={6} key={index}>
          <Box sx={{ position: 'relative' }}>
            <CardMedia
              component="img"
              sx={{
                height: 98,
                objectFit: 'cover',
              }}
              image={media.url}
            />
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: 'white',
                opacity: 0.7
              }}
            >
              {media.type === 'video' ? (
                <PlayCircleIcon sx={{ fontSize: 22 }} />
              ) : (
                <CameraAltIcon sx={{ fontSize: 22 }} />
              )}
            </Box>
            {totalMedia > 4 && index === 3 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '1.5rem',
                  fontWeight: 'bold'
                }}
              >
                +{totalMedia - 4}
              </Box>
            )}
          </Box>
        </Grid>
      ))}
    </Grid>
  );
};

const DownloadHistory: React.FC<DownloadHistoryProps> = ({
  history,
  onItemClick,
  onClearHistory,
  onDeleteItem
}) => {
  const handleCopyLink = (e: React.MouseEvent, url: string) => {
    e.stopPropagation();
    navigator.clipboard.writeText(url);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Download History</Typography>
        {history.length > 0 && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={onClearHistory}
          >
            Clear History
          </Button>
        )}
      </Box>

      <Grid container spacing={2}>
        <AnimatePresence>
          {history.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.url + item.timestamp}>
              <motion.div
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.3 }}
              >
                <Card
                  sx={{
                    cursor: 'pointer',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transform: 'translateY(0)',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)'
                    }
                  }}
                  onClick={() => onItemClick(item)}
                >
                  <Box sx={{ position: 'relative' }}>
                    <MediaGrid item={item} />
                  </Box>
                  <CardContent sx={{ 
                    p: 2,
                    '&:last-child': { pb: 2 },
                    display: 'flex',
                    flexDirection: 'column',
                    height: 160,
                    flex: 'none' // Prevent flex growth
                  }}>
                    <Box sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%'
                    }}>
                      {item.author && (
                        <Typography variant="subtitle2" sx={{ 
                          fontWeight: 'bold',
                          mb: 0.5
                        }}>
                          {item.author.startsWith('@') ? (
                            <Box
                              component="span"
                              onClick={(e) => {
                                e.stopPropagation();
                                const username = item.author?.substring(1);
                                if (username) {
                                  window.open(`https://x.com/${username}`, '_blank');
                                }
                              }}
                              sx={{
                                cursor: 'pointer',
                                color: 'inherit',
                                transition: 'color 0.2s ease',
                                '&:hover': {
                                  color: 'primary.main'
                                }
                              }}
                            >
                              {item.author}
                            </Box>
                          ) : item.author}
                        </Typography>
                      )}
                      
                      {/* Tweet content with flex-grow to fill space */}
                      <Box sx={{ flex: 1, minHeight: 0, mb: 1 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: 1.4
                          }}
                        >
                          {item.title}
                        </Typography>
                      </Box>
                      
                      {/* Bottom row with date and icons */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <Typography 
                          variant="caption" 
                          color="textSecondary"
                        >
                          {new Date(item.timestamp).toLocaleString(undefined, {
                            year: 'numeric',
                            month: 'numeric',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: 'numeric',
                            hour12: true
                          })}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <IconButton
                            onClick={(e) => handleCopyLink(e, item.url)}
                            sx={{
                              width: 20,
                              height: 20,
                              padding: 0,
                              color: 'text.secondary',
                              '&:hover': {
                                backgroundColor: 'transparent',
                                color: 'primary.main'
                              }
                            }}
                          >
                            <LinkIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteItem?.(item);
                            }}
                            sx={{
                              width: 16,
                              height: 16,
                              padding: 0,
                              color: 'text.secondary',
                              '&:hover': {
                                backgroundColor: 'transparent',
                                color: 'error.main'
                              }
                            }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(item.url, '_blank');
                            }}
                            sx={{
                              width: 20,
                              height: 20,
                              padding: 0,
                              '&:hover': {
                                backgroundColor: 'transparent'
                              }
                            }}
                          >
                            <img
                              src="/xicon.webp"
                              alt="View on X"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                              }}
                            />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </AnimatePresence>
      </Grid>
    </Box>
  );
};

export default DownloadHistory; 